import React from 'react';
import {
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  useMediaQuery,
  Theme,
  Tooltip,
  IconButton,
  Popover,
  Box,
  Button,
  ButtonProps,
  Typography,
} from '@mui/material';
import { DatagridConfigurable } from 'react-admin';
import { useResourceContext, useStore, useTranslate } from 'ra-core';
import { ConfigurableDatagridColumn } from 'ra-ui-materialui/src/list/datagrid/DatagridConfigurable';
import ViewWeekIcon from '@mui/icons-material/ViewWeek';
import { FieldToggle } from 'ra-ui-materialui/src/preferences';
import { styled } from '@mui/material/styles';

const GroupedSelectColumnsButton = (props: SelectColumnsButtonProps) => {
  const { preferenceKey } = props;

  const resource = useResourceContext(props);
  const finalPreferenceKey = preferenceKey || `${resource}.datagrid`;

  const [anchorEl, setAnchorEl] = React.useState(null);
  const [availableColumns, setAvailableColumns] = useStore<ConfigurableDatagridColumn[]>(
    `preferences.${finalPreferenceKey}.availableColumns`,
    []
  );

  //const group0 = ['0','1'];
  const defaultColumns = ['0', '1', '2'];
  const editColumn = ['51'];
  const group0 = ['13', '14', '15', '16', '17', '18', '19', '20', '21', '22', '23', '24'];
  const group1 = ['2', '4', '5', '6', '7', '8', '9', '10', '11', '12'];
  const group2 = ['25', '26', '27', '28', '29'];
  const group3 = ['30'];
  const group4 = ['31', '32', '33', '34', '35'];
  const group5 = ['36', '37', '38', '39'];
  const group6 = ['40', '41', '42'];
  const group7 = ['43', '44', '46', '47', '48', '49', '50'];

  const customGroups = [
    { index: '0', source: group0, label: 'Base View' },
    { index: '1', source: group1, label: 'Expanded Client Info' },
    { index: '2', source: group2, label: 'PA Amounts' },
    { index: '3', source: group3, label: 'True Assist' },
    { index: '4', source: group4, label: 'True Advocates' },
    { index: '5', source: group5, label: 'True Codes' },
    { index: '6', source: group6, label: 'True Genomics' },
    { index: '7', source: group7, label: 'Other Fee/Info' },
  ];

  const [groups, setGroups] = useStore<string[]>(`preferences.${finalPreferenceKey}.groups`, []);
  const [availableGroups, setAvailableGroups] = useStore(
    `preferences.${finalPreferenceKey}.availableGroups`,
    customGroups.map((group) => group)
  );

  const [omit] = useStore<string[]>(`preferences.${finalPreferenceKey}.omit`, []);
  const [columns, setColumns] = useStore<string[]>(
    `preferences.${finalPreferenceKey}.columns`,
    availableColumns
      .filter((column) => (column.source ? !omit?.includes(column.source) : true))
      .map((column) => column.index)
  );

  const translate = useTranslate();
  const isXSmall = useMediaQuery((theme: Theme) => theme.breakpoints.down('sm'));

  const title = translate('ra.action.select_columns', { _: 'Columns' });

  const handleClick = (event: any): void => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = (): void => {
    setAnchorEl(null);
  };

  const handleToggle = (event: any) => {
    if (event.target.checked) {
      let selectedColumns = availableGroups[parseInt(event.target.name)].source;
      setColumns(
        availableColumns
          .filter((column) => selectedColumns.includes(column.index) || columns.includes(column.index))
          .map((column) => column.index)
      );
      setGroups(
        availableGroups
          .filter((group) => group.index === event.target.name || groups.includes(group.index))
          .map((group) => group.index)
      );
    } else {
      const updatedGroups = groups.filter((group) => group !== event.target.name); // Remove toggled group
      setGroups(updatedGroups); // Update active groups

      // Recompute columns based on active groups
      const updatedColumns = Array.from(
        new Set([
          ...defaultColumns, // Always include default columns
          ...updatedGroups.flatMap((group) => availableGroups[parseInt(group)]?.source || []),
          ...editColumn,
        ])
      );

      setColumns(updatedColumns); // Update visible columns
    }
  };

  const handleMove = (index1: any, index2: any) => {
    const index1Pos = availableColumns.findIndex(
      // eslint-disable-next-line eqeqeq
      (field) => field.index == index1
    );
    const index2Pos = availableColumns.findIndex(
      // eslint-disable-next-line eqeqeq
      (field) => field.index == index2
    );
    if (index1Pos === -1 || index2Pos === -1) {
      return;
    }
    let newAvailableColumns;
    if (index1Pos > index2Pos) {
      newAvailableColumns = [
        ...availableColumns.slice(0, index2Pos),
        availableColumns[index1Pos],
        ...availableColumns.slice(index2Pos, index1Pos),
        ...availableColumns.slice(index1Pos + 1),
      ];
    } else {
      newAvailableColumns = [
        ...availableColumns.slice(0, index1Pos),
        ...availableColumns.slice(index1Pos + 1, index2Pos + 1),
        availableColumns[index1Pos],
        ...availableColumns.slice(index2Pos + 1),
      ];
    }
    setAvailableColumns(newAvailableColumns);
    setColumns((columns) =>
      newAvailableColumns.filter((column) => columns.includes(column.index)).map((column) => column.index)
    );
  };

  return (
    <>
      {isXSmall ? (
        <Tooltip title={title}>
          <IconButton
            aria-label={title}
            color="primary"
            onClick={handleClick}
            size="large"
            {...sanitizeRestProps(props)}
          >
            <ViewWeekIcon />
          </IconButton>
        </Tooltip>
      ) : (
        <StyledButton size="small" onClick={handleClick} startIcon={<ViewWeekIcon />} {...sanitizeRestProps(props)}>
          {title}
        </StyledButton>
      )}
      <Popover
        open={Boolean(anchorEl)}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
      >
        <Box component="ul" p={1} my={0}>
          {availableGroups.map((group) => (
            <FieldToggle
              key={group.index}
              source={group.source}
              label={group.label}
              index={group.index}
              name={group.label}
              selected={groups.includes(group.index)}
              onToggle={handleToggle}
              onMove={handleMove}
            />
          ))}
        </Box>
      </Popover>
    </>
  );
};

const StyledButton = styled(Button, {
  name: 'RaSelectColumnsButton',
  overridesResolver: (props, styles) => styles.root,
})({
  '&.MuiButton-sizeSmall': {
    // fix for icon misalignment on small buttons, see https://github.com/mui/material-ui/pull/30240
    lineHeight: 1.5,
  },
});

const sanitizeRestProps = ({ resource, preferenceKey, ...rest }: SelectColumnsButtonProps): ButtonProps => rest;

export interface SelectColumnsButtonProps extends ButtonProps {
  resource?: string;
  preferenceKey?: string;
}

export default GroupedSelectColumnsButton;
